import { createClient } from '@/api/supabase'
import { USE_MOCK } from '@/config'
import i18n from '@/utils/i18n'

export const NOTIFICATION_EVENT_TYPES = [
  'order.created',
  'order.accepted',
  'order.inspector_arrived',
  'order.inspection_started',
  'order.appraisal_ready',
  'order.price_set',
  'order.dkp_sent',
  'order.payment_sent',
  'order.completed',
  'order.cancelled',
  'order.inspection_refused',
  'order.buyout_refused',
  'test.ping',
] as const

export const TRIGGER_EVENT_TYPES = NOTIFICATION_EVENT_TYPES.filter((e) => e !== 'test.ping')

export type NotificationSource = 'trigger' | 'broadcast' | 'manual_resend'

export const NOTIFICATION_SOURCES: NotificationSource[] = ['trigger', 'broadcast', 'manual_resend']

export interface NotificationOutboxRow {
  id: string
  sent_at: string | null
  created_at: string
  event_type: string
  source: NotificationSource
  order_id: string | null
  broadcast_id: string | null
  user_id: string
  delivered_at: string | null
  opened_at: string | null
  error: string | null
  status: string
  fcm_message_id: string | null
  recipient_full_name: string | null
  recipient_role: string | null
  order_label: string | null
}

export interface NotificationLogParams {
  page: number
  createdFrom: string
  createdTo: string
  eventTypes: string[]
  sources: string[]
  errorsOnly: boolean
  /** Поиск по ФИО, телефону, заказу, типу события, источнику, статусу, ошибке, FCM id, UUID */
  search: string
}

export interface NotificationLogResult {
  items: NotificationOutboxRow[]
  total: number
}

export interface NotificationBroadcastListItem {
  id: string
  title: string
  body: string
  roles: string[]
  recipient_count: number
  created_at: string
  created_by: string
  creator_full_name: string | null
  sent_count: number
  failed_count: number
  delivered_count: number
  opened_count: number
}

export interface BroadcastListResult {
  items: NotificationBroadcastListItem[]
  total: number
}

export interface PushSubscriptionRow {
  user_id: string
  full_name: string | null
  role: string | null
  platforms: string[]
  app_versions: string[]
  last_seen_at: string | null
  token_count: number
  stale: boolean
}

export interface PushSubscriptionsParams {
  page: number
  roleFilter: string[]
  platform: 'all' | 'ios' | 'android'
  staleOnly: boolean
  search: string
}

export interface PushSubscriptionsResult {
  items: PushSubscriptionRow[]
  total: number
  summary: {
    usersWithTokens: number
    totalTokens: number
    iosTokens: number
    androidTokens: number
    staleUsers: number
  }
}

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i

function shortOrderLabel(orderId: string | null): string | null {
  if (!orderId) return null
  return orderId.replace(/-/g, '').slice(0, 8).toUpperCase()
}

function escapeIlike(s: string): string {
  return s.replace(/\\/g, '\\\\').replace(/%/g, '\\%').replace(/_/g, '\\_').replace(/,/g, '')
}

function uniq(ids: string[]): string[] {
  return [...new Set(ids)]
}

const MAX_IDS_IN_FILTER = 400

async function buildOutboxSearchOrFilter(
  supabase: ReturnType<typeof createClient>,
  raw: string,
): Promise<string | null> {
  const q = raw.trim()
  if (!q) return null

  const pat = `%${escapeIlike(q)}%`
  const parts: string[] = [
    `event_type.ilike.${pat}`,
    `source.ilike.${pat}`,
    `status.ilike.${pat}`,
    `error.ilike.${pat}`,
    `fcm_message_id.ilike.${pat}`,
  ]

  if (UUID_RE.test(q)) {
    parts.push(`id.eq.${q}`, `user_id.eq.${q}`, `order_id.eq.${q}`, `broadcast_id.eq.${q}`)
  }

  const [{ data: profs }, { data: orders }] = await Promise.all([
    supabase.from('profiles').select('id').or(`full_name.ilike.${pat},phone.ilike.${pat}`).limit(MAX_IDS_IN_FILTER),
    supabase.from('orders').select('id').or(`brand.ilike.${pat},model.ilike.${pat}`).limit(MAX_IDS_IN_FILTER),
  ])

  const userIds = uniq((profs ?? []).map((p) => p.id as string).filter(Boolean))
  const orderIds = uniq((orders ?? []).map((o) => o.id as string).filter(Boolean))

  if (userIds.length > 0) {
    parts.push(`user_id.in.(${userIds.join(',')})`)
  }
  if (orderIds.length > 0) {
    parts.push(`order_id.in.(${orderIds.join(',')})`)
  }

  return parts.join(',')
}

export async function listNotificationOutbox(
  params: NotificationLogParams,
): Promise<NotificationLogResult> {
  if (USE_MOCK) {
    return { items: [], total: 0 }
  }
  const supabase = createClient()
  const pageSize = 50
  const from = (params.page - 1) * pageSize
  const to = from + pageSize - 1

  const searchOr = await buildOutboxSearchOrFilter(supabase, params.search)

  let q = supabase
    .from('notification_outbox')
    .select(
      'id, sent_at, created_at, event_type, source, order_id, broadcast_id, user_id, delivered_at, opened_at, error, status, fcm_message_id',
      { count: 'exact' },
    )
    .gte('created_at', params.createdFrom)
    .lte('created_at', params.createdTo)
    .order('created_at', { ascending: false })

  if (params.eventTypes.length > 0) {
    q = q.in('event_type', params.eventTypes)
  }
  if (params.sources.length > 0) {
    q = q.in('source', params.sources)
  }
  if (params.errorsOnly) {
    q = q.not('error', 'is', null)
  }
  if (searchOr) {
    q = q.or(searchOr)
  }

  const { data: rows, error, count } = await q.range(from, to)
  if (error) throw new Error(error.message)

  const raw = rows ?? []
  const userIds = [...new Set(raw.map((r) => r.user_id as string))]
  const orderIds = [
    ...new Set(
      raw.map((r) => r.order_id as string | null).filter((x): x is string => Boolean(x)),
    ),
  ]

  const profileMap = new Map<string, { full_name: string; role: string }>()
  if (userIds.length > 0) {
    const { data: profs, error: pErr } = await supabase
      .from('profiles')
      .select('id, full_name, role')
      .in('id', userIds)
    if (pErr) throw new Error(pErr.message)
    for (const p of profs ?? []) {
      profileMap.set(p.id as string, {
        full_name: (p.full_name as string) ?? '',
        role: (p.role as string) ?? '',
      })
    }
  }

  const orderMap = new Map<string, string>()
  if (orderIds.length > 0) {
    const { data: orders, error: oErr } = await supabase
      .from('orders')
      .select('id, brand, model')
      .in('id', orderIds)
    if (oErr) throw new Error(oErr.message)
    for (const o of orders ?? []) {
      const label = `${(o.brand as string) ?? ''} ${(o.model as string) ?? ''}`.trim()
      orderMap.set(
        o.id as string,
        label.length > 0 ? label : shortOrderLabel(o.id as string) ?? (o.id as string),
      )
    }
  }

  const items: NotificationOutboxRow[] = raw.map((r) => {
    const prof = profileMap.get(r.user_id as string)
    return {
      id: r.id as string,
      sent_at: (r.sent_at as string | null) ?? null,
      created_at: r.created_at as string,
      event_type: r.event_type as string,
      source: r.source as NotificationSource,
      order_id: (r.order_id as string | null) ?? null,
      broadcast_id: (r.broadcast_id as string | null) ?? null,
      user_id: r.user_id as string,
      delivered_at: (r.delivered_at as string | null) ?? null,
      opened_at: (r.opened_at as string | null) ?? null,
      error: (r.error as string | null) ?? null,
      status: r.status as string,
      fcm_message_id: (r.fcm_message_id as string | null) ?? null,
      recipient_full_name: prof?.full_name ?? null,
      recipient_role: prof?.role ?? null,
      order_label: r.order_id
        ? orderMap.get(r.order_id as string) ?? shortOrderLabel(r.order_id as string)
        : null,
    }
  })

  return {
    items,
    total: count ?? items.length,
  }
}

export async function resendPush(outboxId: string): Promise<{ new_outbox_id: string }> {
  if (USE_MOCK) {
    throw new Error(i18n.t('apiErr.notif.mockResendUnavailable'))
  }
  const supabase = createClient()
  const { data, error } = await supabase.functions.invoke<{ ok?: boolean; new_outbox_id?: string; reason?: string }>(
    'resend-push',
    { body: { outbox_id: outboxId } },
  )
  if (error) throw new Error(error.message)
  if (!data?.ok || !data.new_outbox_id) {
    throw new Error(data?.reason === 'no_active_token' ? i18n.t('apiErr.notif.noActiveToken') : i18n.t('apiErr.notif.sendFailed'))
  }
  return { new_outbox_id: data.new_outbox_id }
}

function aggregateBroadcastStats(
  rows: { broadcast_id: string | null; status: string; delivered_at: string | null; opened_at: string | null }[],
): Map<string, { sent: number; failed: number; delivered: number; opened: number }> {
  const m = new Map<string, { sent: number; failed: number; delivered: number; opened: number }>()
  for (const r of rows) {
    const bid = r.broadcast_id as string | null
    if (!bid) continue
    let e = m.get(bid)
    if (!e) {
      e = { sent: 0, failed: 0, delivered: 0, opened: 0 }
      m.set(bid, e)
    }
    const st = (r.status as string) ?? ''
    if (st === 'sent') e.sent += 1
    if (st === 'failed' || st === 'invalid_token') e.failed += 1
    if (r.delivered_at) e.delivered += 1
    if (r.opened_at) e.opened += 1
  }
  return m
}

export async function listBroadcasts(page: number): Promise<BroadcastListResult> {
  if (USE_MOCK) return { items: [], total: 0 }
  const supabase = createClient()
  const pageSize = 20
  const from = (page - 1) * pageSize
  const to = from + pageSize - 1

  const { data: broadcasts, error, count } = await supabase
    .from('notification_broadcasts')
    .select('id, title, body, roles, recipient_count, created_at, created_by', { count: 'exact' })
    .order('created_at', { ascending: false })
    .range(from, to)

  if (error) throw new Error(error.message)
  const list = broadcasts ?? []
  const ids = list.map((b) => b.id as string)

  let statsMap = new Map<string, { sent: number; failed: number; delivered: number; opened: number }>()
  if (ids.length > 0) {
    const { data: outRows, error: oErr } = await supabase
      .from('notification_outbox')
      .select('broadcast_id, status, delivered_at, opened_at')
      .in('broadcast_id', ids)
    if (oErr) throw new Error(oErr.message)
    statsMap = aggregateBroadcastStats(outRows ?? [])
  }

  const creatorIds = uniq(list.map((b) => b.created_by as string))
  const creatorMap = new Map<string, string>()
  if (creatorIds.length > 0) {
    const { data: profs, error: pErr } = await supabase
      .from('profiles')
      .select('id, full_name')
      .in('id', creatorIds)
    if (pErr) throw new Error(pErr.message)
    for (const p of profs ?? []) {
      creatorMap.set(p.id as string, ((p.full_name as string) ?? '').trim() || '—')
    }
  }

  const items: NotificationBroadcastListItem[] = list.map((b) => {
    const id = b.id as string
    const st = statsMap.get(id) ?? { sent: 0, failed: 0, delivered: 0, opened: 0 }
    return {
      id,
      title: b.title as string,
      body: b.body as string,
      roles: (b.roles as string[]) ?? [],
      recipient_count: (b.recipient_count as number) ?? 0,
      created_at: b.created_at as string,
      created_by: b.created_by as string,
      creator_full_name: creatorMap.get(b.created_by as string) ?? null,
      sent_count: st.sent,
      failed_count: st.failed,
      delivered_count: st.delivered,
      opened_count: st.opened,
    }
  })

  return { items, total: count ?? items.length }
}

export async function getBroadcastRecipients(broadcastId: string): Promise<NotificationOutboxRow[]> {
  if (USE_MOCK) return []
  const supabase = createClient()
  const { data: rows, error } = await supabase
    .from('notification_outbox')
    .select(
      'id, sent_at, created_at, event_type, source, order_id, broadcast_id, user_id, delivered_at, opened_at, error, status, fcm_message_id',
    )
    .eq('broadcast_id', broadcastId)
    .order('created_at', { ascending: true })

  if (error) throw new Error(error.message)
  const raw = rows ?? []
  const userIds = [...new Set(raw.map((r) => r.user_id as string))]
  const orderIds = [
    ...new Set(
      raw.map((r) => r.order_id as string | null).filter((x): x is string => Boolean(x)),
    ),
  ]

  const profileMap = new Map<string, { full_name: string; role: string }>()
  if (userIds.length > 0) {
    const { data: profs, error: pErr } = await supabase
      .from('profiles')
      .select('id, full_name, role')
      .in('id', userIds)
    if (pErr) throw new Error(pErr.message)
    for (const p of profs ?? []) {
      profileMap.set(p.id as string, {
        full_name: (p.full_name as string) ?? '',
        role: (p.role as string) ?? '',
      })
    }
  }

  const orderMap = new Map<string, string>()
  if (orderIds.length > 0) {
    const { data: orders, error: oErr } = await supabase
      .from('orders')
      .select('id, brand, model')
      .in('id', orderIds)
    if (oErr) throw new Error(oErr.message)
    for (const o of orders ?? []) {
      const label = `${(o.brand as string) ?? ''} ${(o.model as string) ?? ''}`.trim()
      orderMap.set(
        o.id as string,
        label.length > 0 ? label : shortOrderLabel(o.id as string) ?? (o.id as string),
      )
    }
  }

  return raw.map((r) => {
    const prof = profileMap.get(r.user_id as string)
    return {
      id: r.id as string,
      sent_at: (r.sent_at as string | null) ?? null,
      created_at: r.created_at as string,
      event_type: r.event_type as string,
      source: r.source as NotificationSource,
      order_id: (r.order_id as string | null) ?? null,
      broadcast_id: (r.broadcast_id as string | null) ?? null,
      user_id: r.user_id as string,
      delivered_at: (r.delivered_at as string | null) ?? null,
      opened_at: (r.opened_at as string | null) ?? null,
      error: (r.error as string | null) ?? null,
      status: r.status as string,
      fcm_message_id: (r.fcm_message_id as string | null) ?? null,
      recipient_full_name: prof?.full_name ?? null,
      recipient_role: prof?.role ?? null,
      order_label: r.order_id
        ? orderMap.get(r.order_id as string) ?? shortOrderLabel(r.order_id as string)
        : null,
    }
  })
}

export async function getBroadcastMeta(broadcastId: string): Promise<{
  id: string
  title: string
  body: string
  roles: string[]
  recipient_count: number
  created_at: string
  created_by: string
  creator_full_name: string | null
}> {
  if (USE_MOCK) {
    throw new Error(i18n.t('apiErr.notif.mockBroadcastUnavailable'))
  }
  const supabase = createClient()
  const { data: b, error } = await supabase
    .from('notification_broadcasts')
    .select('id, title, body, roles, recipient_count, created_at, created_by')
    .eq('id', broadcastId)
    .maybeSingle()
  if (error) throw new Error(error.message)
  if (!b) throw new Error(i18n.t('apiErr.notif.broadcastNotFound'))

  let creatorName: string | null = null
  const cb = b.created_by as string
  if (cb) {
    const { data: p } = await supabase.from('profiles').select('full_name').eq('id', cb).maybeSingle()
    creatorName = ((p?.full_name as string) ?? '').trim() || null
  }

  return {
    id: b.id as string,
    title: b.title as string,
    body: b.body as string,
    roles: (b.roles as string[]) ?? [],
    recipient_count: (b.recipient_count as number) ?? 0,
    created_at: b.created_at as string,
    created_by: cb,
    creator_full_name: creatorName,
  }
}

const STALE_MS = 30 * 24 * 60 * 60 * 1000

export async function listPushSubscriptions(
  params: PushSubscriptionsParams,
): Promise<PushSubscriptionsResult> {
  if (USE_MOCK) {
    return {
      items: [],
      total: 0,
      summary: { usersWithTokens: 0, totalTokens: 0, iosTokens: 0, androidTokens: 0, staleUsers: 0 },
    }
  }
  const supabase = createClient()
  const pageSize = 50

  let tokenQuery = supabase
    .from('device_tokens')
    .select('user_id, platform, app_version, last_seen_at', { count: 'exact' })

  if (params.platform !== 'all') {
    tokenQuery = tokenQuery.eq('platform', params.platform)
  }

  const { data: allTokens, error: tErr } = await tokenQuery
  if (tErr) throw new Error(tErr.message)
  const tokens = allTokens ?? []
  const now = Date.now()

  const byUser = new Map<
    string,
    { platforms: Set<string>; versions: Set<string>; lastSeen: number | null; count: number }
  >()
  let iosTotal = 0
  let androidTotal = 0
  for (const t of tokens) {
    const uid = t.user_id as string
    const plat = (t.platform as string) ?? ''
    if (plat === 'ios') iosTotal += 1
    if (plat === 'android') androidTotal += 1
    let g = byUser.get(uid)
    if (!g) {
      g = { platforms: new Set(), versions: new Set(), lastSeen: null, count: 0 }
      byUser.set(uid, g)
    }
    g.count += 1
    if (plat) g.platforms.add(plat)
    const v = (t.app_version as string | null)?.trim()
    if (v) g.versions.add(v)
    const ls = t.last_seen_at ? new Date(t.last_seen_at as string).getTime() : null
    if (ls != null) {
      g.lastSeen = g.lastSeen == null ? ls : Math.max(g.lastSeen, ls)
    }
  }

  let userIds = [...byUser.keys()]
  if (params.roleFilter.length > 0 && userIds.length > 0) {
    const { data: profs, error: pErr } = await supabase
      .from('profiles')
      .select('id, role')
      .in('id', userIds)
    if (pErr) throw new Error(pErr.message)
    const allowed = new Set(
      (profs ?? []).filter((p) => params.roleFilter.includes((p.role as string) ?? '')).map((p) => p.id as string),
    )
    userIds = userIds.filter((id) => allowed.has(id))
  }

  const sq = params.search.trim().toLowerCase()
  if (sq && userIds.length > 0) {
    const { data: profs, error: pErr } = await supabase
      .from('profiles')
      .select('id, full_name, phone')
      .in('id', userIds)
    if (pErr) throw new Error(pErr.message)
    userIds = (profs ?? [])
      .filter((p) => {
        const name = ((p.full_name as string) ?? '').toLowerCase()
        const phone = ((p.phone as string) ?? '').toLowerCase()
        return name.includes(sq) || phone.includes(sq) || (p.id as string).toLowerCase().includes(sq)
      })
      .map((p) => p.id as string)
  }

  const rows: PushSubscriptionRow[] = []
  for (const uid of userIds) {
    const g = byUser.get(uid)
    if (!g) continue
    const lastIso =
      g.lastSeen != null ? new Date(g.lastSeen).toISOString() : null
    const stale = g.lastSeen != null && now - g.lastSeen > STALE_MS
    if (params.staleOnly && !stale) continue
    rows.push({
      user_id: uid,
      full_name: null,
      role: null,
      platforms: [...g.platforms],
      app_versions: [...g.versions],
      last_seen_at: lastIso,
      token_count: g.count,
      stale,
    })
  }

  rows.sort((a, b) => (b.last_seen_at ?? '').localeCompare(a.last_seen_at ?? ''))

  if (rows.length > 0) {
    const { data: profRows } = await supabase
      .from('profiles')
      .select('id, full_name, role')
      .in(
        'id',
        rows.map((r) => r.user_id),
      )
    const profById = new Map((profRows ?? []).map((p) => [p.id as string, p]))
    for (const r of rows) {
      const p = profById.get(r.user_id)
      if (p) {
        r.full_name = ((p.full_name as string) ?? null) || null
        r.role = ((p.role as string) ?? null) || null
      }
    }
  }

  const staleUsersAll = [...byUser.entries()].filter(([, g]) => {
    if (g.lastSeen == null) return false
    return now - g.lastSeen > STALE_MS
  }).length

  const summary = {
    usersWithTokens: byUser.size,
    totalTokens: tokens.length,
    iosTokens: iosTotal,
    androidTokens: androidTotal,
    staleUsers: staleUsersAll,
  }

  const total = rows.length
  const from = (params.page - 1) * pageSize
  const slice = rows.slice(from, from + pageSize)

  return { items: slice, total, summary }
}

export interface NotificationTemplateRow {
  event_type: string
  role: string
  is_enabled: boolean
  title: string
  body: string
}

export async function listNotificationTemplates(): Promise<NotificationTemplateRow[]> {
  if (USE_MOCK) return []
  const supabase = createClient()
  const { data, error } = await supabase
    .from('notification_templates')
    .select('event_type, role, is_enabled, title, body')
    .neq('event_type', 'test.ping')
  if (error) throw new Error(error.message)
  return (data ?? []) as NotificationTemplateRow[]
}

export async function setEventTemplatesAllOff(eventType: string): Promise<void> {
  if (USE_MOCK) return
  const supabase = createClient()
  const { error } = await supabase
    .from('notification_templates')
    .update({ is_enabled: false })
    .eq('event_type', eventType)
  if (error) throw new Error(error.message)
}

export async function setEventTemplatesOnForSeededRoles(eventType: string): Promise<void> {
  if (USE_MOCK) return
  const supabase = createClient()
  const { data: rows, error: fetchErr } = await supabase
    .from('notification_templates')
    .select('role, title, body')
    .eq('event_type', eventType)
  if (fetchErr) throw new Error(fetchErr.message)
  const list = rows ?? []
  const withText = list.filter(
    (r) => (r.title as string)?.trim()?.length > 0 || (r.body as string)?.trim()?.length > 0,
  )
  if (withText.length === 0) {
    throw new Error('NO_TEMPLATE_TEXT')
  }
  for (const r of list) {
    const has = (r.title as string)?.trim() || (r.body as string)?.trim()
    const { error } = await supabase
      .from('notification_templates')
      .update({ is_enabled: Boolean(has) })
      .eq('event_type', eventType)
      .eq('role', r.role as string)
    if (error) throw new Error(error.message)
  }
}
